/* eslint-disable no-template-curly-in-string */
/* eslint-disable react-hooks/exhaustive-deps */
//import packages
import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import {
  Layout,
  Form,
  Input,
  Button,
  Row,
  Col,
  Typography,
  message,
  Modal,
  Spin,
  Divider,
  List,
} from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { GoogleLogin } from "@react-oauth/google";
import styled from "styled-components";

//import icons
import LogoIcon from "../assets/icons/LogoIcon";

//import functions
import auth from "../auth/authorization";
import { useAuthentication } from "../context/AuthProvider";

import { UserRoleEnum } from "../types/enums";

const { Content } = Layout;

const StyledOuterLayout = styled(Layout)`
  min-height: 100vh;
  align-items: center;
  justify-content: center;
`;

const StyledContent = styled(Content)`
  flex: unset;
`;

const StyledComponentWrapper = styled.div`
  width: 100%;
  max-width: 514px;
  @media (max-width: 1200px) {
    max-width: 720px;
  }
  @media (max-width: 788px), (max-width: 922px) {
    max-width: 580px;
  }
  @media (max-width: 720px) {
    max-width: 320px;
  }
`;
const Title = styled(Typography.Title)`
  && {
    font-size: 28px;
    font-weight: 700;
    text-align: center;
  }
`;

const StyledButton = styled(Button)`
  margin: 12px 0 0;
  width: 100%;
  height: 52px;
  font-size: 16px;
  font-weight: 700;
`;

const StyledLinkButton = styled(Button)`
  width: 100%;
  font-size: 16px;
`;

const StyledInput = styled(Input)`
  font-size: 16px;
  height: 56px;
  border: 1px solid #c5c7d5 !important;
`;

const StyledPasswordField = styled(Input.Password)`
  font-size: 16px;
  height: 56px;
  border: 1px solid #c5c7d5 !important;
  > .ant-input-suffix {
    font-size: 24px;
    margin-left: unset;
    margin: 4px;
  }
`;

const StyledFormItem = styled(Form.Item)`
  text-align: right;
`;

const StyledForgetPasswordButton = styled(Button)`
  margin-left: 8px;
`;

const GoogleLoginWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
`;

const LoginComponent = () => {
  const [loading, setLoading] = useState(false);
  const [formloading, setFormLoading] = useState(false);
  const [forgetPasswordModalVisible, setForgetPasswordModalVisible] = useState(false);
  const [accountCandidates, setAccountCandidates] = useState(null);
  const [pendingCredential, setPendingCredential] = useState(null);
  const history = useHistory();
  const { login, googleLogin, forgotPassword } = useAuthentication();

  const handleGoogleSuccess = async (credential, selectedUserId) => {
    try {
      const result = await googleLogin(credential, selectedUserId);
      if (result?.candidates) {
        setPendingCredential(credential);
        setAccountCandidates(result.candidates);
      }
    } catch {
      // error already surfaced by googleLogin
    }
  };

  const handleAccountSelection = (userId) => {
    handleGoogleSuccess(pendingCredential, userId);
    setAccountCandidates(null);
    setPendingCredential(null);
  };

  const cancelAccountPicker = () => {
    setAccountCandidates(null);
    setPendingCredential(null);
  };

  const reRouteIfAlreadyLoggedIn = () => {
    if (auth.isAuthenticated()) {
      const userType = auth.getUserType();
      if (
        userType === "admin" ||
        userType === "manager" ||
        userType === "admin support" ||
        userType === "super admin" ||
        userType === "provider" ||
        userType === "steadymd admin support" ||
        userType === UserRoleEnum.DEVELOPER || 
        userType === UserRoleEnum.IT_SYSTEM_ADMIN ||
        userType === UserRoleEnum.OPS_ADMIN ||
        userType === UserRoleEnum.OPS_ADMIN_W_IMPERSONATION
      ) {
        history.push("/");
      } else {
        message.error("Login failed. Invalid Email or Password!");
        localStorage.clear();
      }
    } else {
      localStorage.clear();
    }
  };

  useEffect(() => {
    reRouteIfAlreadyLoggedIn();
  }, []);

  const layout = {
    labelCol: {
      span: 12,
    },
    wrapperCol: {
      span: 24,
    },
  };

  const forgetPasswordFormLayout = {
    labelCol: {
      span: 24,
    },
    wrapperCol: {
      span: 24,
    },
  };

  const validateMessages = {
    required: "Please enter your ${label}",
    types: {
      email: "${label} is not valid email!",
    },
  };

  const forgetPasswordValidateMessages = {
    required: "Please enter your email address",
    types: {
      email: "Email address is not valid!",
    },
  };

  const onFinish = (values) => {
    setLoading(true);
    const { user } = values;
    login({
      email: user.email,
      password: user.password
    }).finally(() => setLoading(false));
  };

  const onForgetPasswordFinish = (values) => {
    setFormLoading(true);
    const { forgetPassword } = values;

    forgotPassword(forgetPassword.email)
      .then(() => {
        message.success("Reset link has been sent to your email");
        setForgetPasswordModalVisible(false);
        setFormLoading(false);
      })
      .catch(() => {
        message.error("Failed to find your account! Please check your email or try again later");
        setFormLoading(false);
      });
  };

  const handleForgetPasswordLink = () => {
    setForgetPasswordModalVisible(true);
  };

  const handleForgetPasswordModalCancel = () => {
    setForgetPasswordModalVisible(false);
  };

  return (
    <StyledOuterLayout>
      <StyledContent>
        <StyledComponentWrapper>
          <Form
            {...layout}
            layout={"vertical"}
            name="login-form"
            onFinish={onFinish}
            validateMessages={validateMessages}
          >
            <Title level={2}>
              <LogoIcon width="224px" height="80px" />
            </Title>
            <Row gutter={24}>
              <Col span={24}>
                <Form.Item
                  name={["user", "email"]}
                  label="Email"
                  rules={[
                    {
                      required: true,
                      type: "email",
                    },
                  ]}
                >
                  <StyledInput
                    placeholder="Enter Email Id"
                    disabled={loading}
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name={["user", "password"]}
                  label="Password"
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  <StyledPasswordField
                    placeholder="Enter Password"
                    disabled={loading}
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item wrapperCol={{ ...layout.wrapperCol }}>
                  <StyledButton
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                  >
                    LOGIN
                  </StyledButton>
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item wrapperCol={{ ...layout.wrapperCol }}>
                  <StyledLinkButton
                    type="link"
                    onClick={handleForgetPasswordLink}
                  >
                    Forgotten password?
                  </StyledLinkButton>
                </Form.Item>
              </Col>
            </Row>
          </Form>
          <Divider plain>or</Divider>
          <GoogleLoginWrapper>
            <GoogleLogin
              onSuccess={(credentialResponse) => {
                handleGoogleSuccess(credentialResponse.credential);
              }}
              onError={() => {
                message.error("Google sign-in failed");
              }}
              width="480"
              text="signin_with"
              shape="rectangular"
              size="large"
            />
          </GoogleLoginWrapper>
          <Modal
            open={!!accountCandidates}
            title="Select an account"
            maskClosable={false}
            onCancel={cancelAccountPicker}
            footer={null}
            centered
            destroyOnClose
          >
            <List
              dataSource={accountCandidates || []}
              renderItem={(candidate) => (
                <List.Item
                  actions={[
                    <Button
                      type="primary"
                      onClick={() => handleAccountSelection(candidate._id)}
                    >
                      Select
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    title={`${candidate.firstName || ""} ${candidate.lastName || ""}`.trim() || candidate.email}
                    description={
                      <>
                        <div>{candidate.email}</div>
                        {candidate.userType && <div>{candidate.userType}</div>}
                      </>
                    }
                  />
                </List.Item>
              )}
            />
          </Modal>
          <Modal
            open={forgetPasswordModalVisible}
            title="Find Your Account"
            maskClosable={false}
            closable={false}
            footer={null}
            centered
            destroyOnClose
          >
            <Spin
              spinning={formloading}
              size="large"
              indicator={<LoadingOutlined />}
            >
              <Form
                {...forgetPasswordFormLayout}
                layout={"vertical"}
                name="forget-password-form"
                onFinish={onForgetPasswordFinish}
                validateMessages={forgetPasswordValidateMessages}
              >
                <Row gutter={24}>
                  <Col span={24}>
                    <Form.Item
                      name={["forgetPassword", "email"]}
                      label="Please enter your email address to search for your account."
                      rules={[
                        {
                          required: true,
                          type: "email",
                        },
                      ]}
                    >
                      <Input placeholder="Enter Email Id" disabled={loading} />
                    </Form.Item>
                  </Col>

                  <Col span={24}>
                    <StyledFormItem
                      wrapperCol={{ ...forgetPasswordFormLayout.wrapperCol }}
                    >
                      <StyledForgetPasswordButton
                        type="secondary"
                        size="large"
                        onClick={handleForgetPasswordModalCancel}
                      >
                        Cancel
                      </StyledForgetPasswordButton>
                      <StyledForgetPasswordButton
                        type="primary"
                        htmlType="submit"
                        size="large"
                      >
                        Submit
                      </StyledForgetPasswordButton>
                    </StyledFormItem>
                  </Col>
                </Row>
              </Form>
            </Spin>
          </Modal>
        </StyledComponentWrapper>
      </StyledContent>
    </StyledOuterLayout>
  );
};

export default LoginComponent;
